import { TranslationKey } from '@Localization';
import { v4 } from 'uuid';
import { ExportDataFilter } from '../../projects/projects.model';

export interface IBaseNotification {
  id: string;
  date: string;
  popupType: NotificationPopupType;
  wasRead: boolean;
}

interface IToastNotification extends IBaseNotification {
  level: NotificationLevel;
  body: string | ILocalizable;
  title?: string | ILocalizable;
}

export interface IHubNotification extends IBaseNotification {
  type: NotificationType;
}

export interface IDatasetUploadCompletedNotification extends IHubNotification {
  datasetName: string;
}

export interface IAttachmentsUploadNotification extends IHubNotification {
  uploadedCount: number;
  failedFiles: string[];
}

export interface IDatasetUploadCompletedWithInfoNotification extends IHubNotification {
  datasetName: string;
  rejectedUrls: string[];
}

export interface IDatasetUploadFailedNotification extends IHubNotification {
  datasetName: string;
}

export interface IDatasetUploadAbortedNotification extends IHubNotification {
  datasetName: string;
}

export interface IAccountRechargedNotification extends IHubNotification {
  credits: number;
  newLimit: number;
}

export interface IBatchAnswerQuestionsFailedNotification extends IHubNotification {
  batchNumber: number;
  totalBatchesCount: number;
}

export interface IClarifiedNotification extends IHubNotification {
  projectName: string;
  imageName: string;
}

export interface IJobNotification extends IHubNotification {
  workspaceId: string;
  jobId: string;
  jobType: 'Training' | 'Evaluation';
  jobName: string;
}

export interface IApproachingToCostLimitNotification extends IHubNotification {
  workspaceId: string;
}

export interface IProjectExportFinishedNotification extends IHubNotification {
  projectName: string;
  dataFilterType: ExportDataFilter;
  projectExportId: string;
}

export interface ILongLivingInstancesNotification extends IHubNotification {
  instanceNames: string[];
}

export interface ICalculatedCostsLowerThanExpectedNotification extends IHubNotification {
  workspaces: string[];
}

export interface IFailingJobsNotification extends IHubNotification {
  jobIds: string[];
}

export interface ILocalizable {
  template: string;
  data?: object;
}

export enum NotificationLevel {
  INFO = 'info',
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
}

export enum NotificationPopupType {
  Toast = 'Toast',
  NotificationCenter = 'NotificationCenter',
}

export enum NotificationType {
  DatasetUploadCompleted = 'DatasetUploadCompleted',
  DatasetCompletedWithInfo = 'DatasetCompletedWithInfo',
  DatasetUploadFailed = 'DatasetUploadFailed',
  PaymentPending = 'PaymentPending',
  PaymentFailed = 'PaymentFailed',
  AccountRecharged = 'AccountRecharged',
  DatasetUploadAborted = 'DatasetUploadAborted',
  AttachmentsUploadCompleted = 'AttachmentsUploadCompleted',
  AttachmentsUploadFailed = 'AttachmentsUploadFailed',
  SomeAttachmentsUploadFailed = 'SomeAttachmentsUploadFailed',
  BatchAnswerQuestionsFinished = 'BatchAnswerQuestionsFinished',
  BatchAnswerQuestionsFailed = 'BatchAnswerQuestionsFailed',
  ProjectExportFinished = 'ProjectExportFinished',
  Clarified = 'Clarified',
  JobFinished = 'JobFinished',
  JobRetrying = 'JobRetrying',
  JobFailed = 'JobFailed',
  JobLongRunTime = 'JobLongRunTime',
  ApproachingToCostLimit = 'ApproachingToCostLimit',
  LongLivingInstances = 'LongLivingInstances',
  CalculatedCostsLowerThanExpected = 'CalculatedCostsLowerThanExpected',
  FailingJobsNotification = 'FailingJobs',
}

abstract class BaseNotification implements IBaseNotification {
  id: string;
  date: string;
  wasRead: boolean;

  constructor(public popupType: NotificationPopupType) {
    this.id = v4();
    this.date = new Date().toISOString();
    this.wasRead = false;
  }
}

export class ToastNotification extends BaseNotification implements IToastNotification {
  constructor(
    public level: NotificationLevel,
    public body: TranslationKey | ILocalizable,
    public title?: TranslationKey | ILocalizable
  ) {
    super(NotificationPopupType.Toast);
  }
}

export class HubNotification extends BaseNotification implements IHubNotification {
  constructor(public type: NotificationType) {
    super(NotificationPopupType.NotificationCenter);
  }
}

export class DatasetUploadCompletedNotification extends HubNotification implements IDatasetUploadCompletedNotification {
  constructor(public datasetName: string) {
    super(NotificationType.DatasetUploadCompleted);
  }
}

export class DatasetUploadCompletedWithInfoNotification extends HubNotification implements IDatasetUploadCompletedWithInfoNotification {
  constructor(
    public datasetName: string,
    public rejectedUrls: string[]
  ) {
    super(NotificationType.DatasetCompletedWithInfo);
  }
}

const getAttachmentNotificationType = (failedFiles: string[], uploadedCount: number): NotificationType => {
  if (uploadedCount === 0) {
    return NotificationType.AttachmentsUploadFailed;
  }
  if (failedFiles.length === 0) {
    return NotificationType.AttachmentsUploadCompleted;
  }
  return NotificationType.SomeAttachmentsUploadFailed;
};

export class AttachmentsUploadNotification extends HubNotification implements IAttachmentsUploadNotification {
  constructor(
    public failedFiles: string[],
    public uploadedCount: number
  ) {
    super(getAttachmentNotificationType(failedFiles, uploadedCount));
  }
}

export class DatasetUploadFailedNotification extends HubNotification implements IDatasetUploadFailedNotification {
  constructor(public datasetName: string) {
    super(NotificationType.DatasetUploadFailed);
  }
}

export class DatasetUploadAbortedNotification extends HubNotification implements IDatasetUploadAbortedNotification {
  constructor(public datasetName: string) {
    super(NotificationType.DatasetUploadAborted);
  }
}

export class PolicyLimitExceededNotification extends ToastNotification {
  constructor() {
    super(NotificationLevel.ERROR, 'policy_limit_exceeded_body', '');
  }
}

export class ToManyRequestsNotification extends ToastNotification {
  constructor() {
    super(NotificationLevel.ERROR, 'to_many_requests_body', '');
  }
}
